import { useState } from 'react';
import { refineSectionContent } from '../../../services/rfpCoreService';
import { formatErrorDetail } from '../utils/helperUtils';

/**
 * Hook to manage AI-driven content improvement features
 */
export const useAIFeatures = (rfpId, editableData, activeSection, selectedCell, handleSidePanelEdit, setUpdateStatus, fetchRFPData, setEditableData, activeSectionObj) => {
    const [isRefining, setIsRefining] = useState(false);
    const [aiPrompt, setAiPrompt] = useState('');

    const handleAIImprove = async () => {
        if (!aiPrompt) return;

        let contentToRefine = "";
        if (selectedCell) {
            contentToRefine = selectedCell.value;
        } else {
            const sectionData = editableData[activeSection];
            contentToRefine = typeof sectionData === 'string'
                ? sectionData
                : (Array.isArray(sectionData) ? sectionData.join('\n\n') : JSON.stringify(sectionData));
        }

        if (!contentToRefine) return;

        setIsRefining(true);
        setUpdateStatus(null);

        try {
            const sectionData = editableData[activeSection];
            const sectionId = (sectionData && typeof sectionData === 'object' && sectionData.id) ? sectionData.id : activeSection;

            const response = await refineSectionContent({
                rfp_id: rfpId,
                id: sectionId,
                current_content: contentToRefine,
                refinement_prompt: aiPrompt
            });

            const data = response.data;

            if (response.status === 200) {
                const refinedHtml = data.refined_content || data.content;

                // 1. Immediately update local editableData including sections array
                if (typeof handleSidePanelEdit === 'function') {
                    handleSidePanelEdit(refinedHtml);
                }

                if (typeof setEditableData === 'function') {
                    setEditableData(prev => {
                        if (!prev) return prev;
                        const next = { ...prev, [activeSection]: refinedHtml };
                        if (Array.isArray(next.sections)) {
                            next.sections = next.sections.map(s => {
                                const isMatch = (s.label && s.label === activeSection) ||
                                    (s.key && s.key === activeSection) ||
                                    (s.section_title && s.section_title === activeSection) ||
                                    (s.section_key && s.section_key === activeSection) ||
                                    (s.id && String(s.id) === String(sectionId)) ||
                                    (s.section_id && String(s.section_id) === String(sectionId));
                                if (isMatch) {
                                    return {
                                        ...s,
                                        content: refinedHtml,
                                        draft_content: refinedHtml,
                                        text: refinedHtml
                                    };
                                }
                                return s;
                            });
                        }
                        return next;
                    });
                }

                setAiPrompt('');
                setUpdateStatus({ type: 'success', message: data.message || 'Content refined by AI!' });

                // 2. Fetch fresh data from backend
                if (typeof fetchRFPData === 'function') {
                    await fetchRFPData(true);
                }

                // 3. Perform hard refresh from UI
                const targetSecId = activeSectionObj?.id || activeSectionObj?.section_id || activeSectionObj?._id;
                const targetUrl = targetSecId
                    ? `/proposals/analysis-result/${rfpId}/secid/${targetSecId}`
                    : `/proposals/analysis-result/${rfpId}`;

                if (window.location.pathname === targetUrl) {
                    window.location.reload();
                } else {
                    window.location.href = targetUrl;
                }
            } else {
                setUpdateStatus({ type: 'error', message: formatErrorDetail(data.detail) || 'AI refinement failed.' });
            }
        } catch (err) {
            setUpdateStatus({ type: 'error', message: 'Network error. Please try again.' });
        } finally {
            setIsRefining(false);
            setTimeout(() => setUpdateStatus(null), 3000);
        }
    };

    return {
        isRefining,
        aiPrompt,
        setAiPrompt,
        handleAIImprove
    };
};
